import React, { useRef, useEffect, useState, Fragment } from "react";
import { Formik } from "formik";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  getCtxMerchantAccountUUID,
  getCtxAppResourceAndIDs
} from "utils/mapStateToProps";
import { createMerchantAccountProducts } from "actions/product";
import isEmpty from "lodash/isEmpty";
import { PRODUCT_ORDER_INTENT } from "utils/constants/pricing";
import { CREATE_PRODUCTS_SCHEMA } from "constants/validation";
import {
  prepareCreateProducts,
  getCreateProductsInitialValues
} from "utils/product";
import { withRouter } from "next/router";
import ProductNewList from "../ProductNewList";
import { getDefaultRecurringPricePrimaries } from "components/FormerEditor/common";
import { STATE_KEYS } from "utils/constants/state";
import { secondaryBackButton } from "utils/constants/ui";
import { usePageBuilderContext } from "utils/context";
import Analytics, { CREATE_PRODUCT } from "utils/analytics";
import { getCreateProductTracking } from "utils/tracking/product";
import MenuNavHeader from "components/MenuNavHeader";
import Padder from "components/Padder";
import { getModelIDFromQuery } from "utils/route";
import { APPLICATION } from "utils/constants/models";

const COMPONENT = "ProductNewForm";
const ProductNewForm = ({
  pricePrimaries,
  setFieldValue,
  resourceUUID,
  resource,
  merchantAccountUUID,
  actions
}) => {
  const { tracking } = usePageBuilderContext();
  const [validateOnMount, setValidateOnMount] = useState(false);
  const formRef = useRef(null);
  useEffect(() => {
    formRef.current.validateForm();
    setValidateOnMount(true);
  }, []);

  const resetToProductShow = () => {
    setFieldValue(
      STATE_KEYS.EDITOR.PRODUCT_ORDER_INTENT,
      PRODUCT_ORDER_INTENT.PRODUCT_ORDER
    );
    setFieldValue(STATE_KEYS.EDITOR.PRICING_ACTIVE_PRICE, null);
  };

  const buttonProps = secondaryBackButton({
    onClick: () => resetToProductShow(),
    tooltip: "Back"
  });

  const createProducts = (formValues, formikActions) => {
    const data = prepareCreateProducts(formValues.products);

    const createProductTracking = getCreateProductTracking({
      ...data,
      ...tracking,
      component: COMPONENT
    });
    Analytics.track(CREATE_PRODUCT, createProductTracking);
    actions.createMerchantAccountProducts(
      {
        resourceUUID,
        resource,
        uuid: merchantAccountUUID,
        data
      },
      (err, data) => {
        if (err) {
          formikActions.setErrors(err);
        } else {
          /**
           * Creating a new product will require us to set default primaries for it so that it can be used within the pricing table
           * Set the default primary prices for created products which have prices
           */
          const { prices, products } = data;
          const hasProductsPrices =
            Array.isArray(products) &&
            products.length &&
            Array.isArray(prices) &&
            prices.length;

          if (hasProductsPrices) {
            const newPrimaries = getDefaultRecurringPricePrimaries({
              products,
              prices
            });
            const allPrimaries = [...pricePrimaries, ...newPrimaries];

            setFieldValue(STATE_KEYS.MANIFEST.PRICING_PRIMARIES, allPrimaries);
          }
          formikActions.resetForm({ values: formValues });
        }
        formikActions.setSubmitting(false);
        resetToProductShow();
      }
    );
  };

  return (
    <Formik
      innerRef={formRef}
      initialValues={getCreateProductsInitialValues()}
      validate={async (values) =>
        CREATE_PRODUCTS_SCHEMA.validate(values)
          .then(() => ({}))
          .catch((err) => ({
            [err.path]: err.message
          }))
      }
      onSubmit={createProducts}
    >
      {({ submitForm, isSubmitting, errors }) => {
        return (
          <div className="w-100 db">
            <MenuNavHeader
              buttonProps={buttonProps}
              label="Create"
              action={{
                onClick: submitForm,
                copy: `Save`,
                disabled: !validateOnMount || !isEmpty(errors),
                loading: isSubmitting
              }}
            />
            <ProductNewList name="products" />
            <Padder theme="visible" />
          </div>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = (state, { router }) => {
  const applicationUUID = getModelIDFromQuery(APPLICATION, router.query);
  return {
    ...getCtxAppResourceAndIDs(state, applicationUUID),
    merchantAccountUUID: getCtxMerchantAccountUUID(state)
  };
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createMerchantAccountProducts
    },
    dispatch
  )
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ProductNewForm)
);
